import React from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  VStack,
  Wrap,
  WrapItem,
  Tag,
  Icon,
  Link,
} from '@chakra-ui/react';
import { FaCode, FaLightbulb, FaWrench, FaCertificate } from 'react-icons/fa';

const Skills = () => {
  const skillCategories = [
    {
      title: 'Languages',
      icon: FaCode,
      color: 'yellow',
      bgGradient: 'linear(to-br, #FFC107, #FFD54F)',
      skills: [
        'JavaScript',
        'TypeScript',
        'Python',
        'HTML',
        'CSS',
        'PHP',
        'Java',
        'SQL',
      ],
    },
    {
      title: 'Technologies',
      icon: FaLightbulb,
      color: 'green',
      bgGradient: 'linear(to-br, #4CAF50, #81C784)',
      skills: [
        'React',
        'Node.js',
        'Angular',
        'GraphQL',
        'React Native',
        'Bootstrap',
        'MongoDB',
        'Chakra UI',
        'Express',
        'Next.js',
        'Redux',
        'Firebase',
      ],
    },
    {
      title: 'Tools',
      icon: FaWrench,
      color: 'blue',
      bgGradient: 'linear(to-br, #5BC0EB, #87CEEB)',
      skills: [
        'Git',
        'Postman',
        'Github',
        'PowerShell',
        'VS Code',
        'AWS',
        'GCP',
        'Figma',
        'Jira',
        'Azure',
      ],
    },
    {
      title: 'Certificates',
      icon: FaCertificate,
      color: 'purple',
      bgGradient: 'linear(to-br, #9C27B0, #BA68C8)',
      skills: [
        {
          name: 'Microsoft Certified: Azure Fundamentals',
          link: 'https://learn.microsoft.com/api/credentials/share/en-gb/SharapovRamil-8894/D54808FDC695B9D8?sharingId=2DE866C4DB0018C1'
        },
        {
          name: 'Microsoft 365 Certified',
          link: 'https://learn.microsoft.com/en-gb/users/sharapovramil-8894/credentials/19e3308dc7c0fd?ref=https%3A%2F%2Fwww.google.com%2F'
        },
        {
          name: 'Full-Stack Web Development - Concordia University',
          link: '/Diploma_Concordia.pdf'
        },
      ],
    },
  ];

  return (
    <Box as="section" id="skills" bg="white" py={20} position="relative">
      {/* Dot Pattern Decoration */}
      <Box
        position="absolute"
        bottom="40px"
        right="40px"
        width="120px"
        height="120px"
        opacity={0.1}
        pointerEvents="none"
      >
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <pattern
            id="dot-pattern"
            x="0"
            y="0"
            width="20"
            height="20"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="2" cy="2" r="2" fill="#5BC0EB" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#dot-pattern)" />
        </svg>
      </Box>

      <Container maxW="1200px">
        <VStack spacing={12} align="stretch" w="100%">
          {/* Section Title */}
          <Heading
            as="h2"
            fontSize={{ base: '2.9rem', md: 'clamp(3.6rem, 5vw, 4.6rem)' }}
            fontWeight="800"
            color="#2d3436"
            lineHeight="1.1"
            textAlign={{ base: 'center', md: 'left' }}
          >
            My{' '}
            <Box
              as="span"
              position="relative"
              display="inline-block"
              zIndex={1}
              color="#2d3436"
              _before={{
                content: '""',
                position: 'absolute',
                bottom: '5px',
                left: '-5px',
                right: '-5px',
                height: '18px',
                bg: '#FFB84D',
                zIndex: -1,
                transform: 'skewY(-1deg)',
                borderRadius: '4px',
              }}
            >
              Skills
            </Box>
          </Heading>

          {/* Skills Grid */}
          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 2 }}
            spacing={10}
            w="100%"
            mt={8}
          >
            {skillCategories.map((category, index) => (
              <Box
                key={index}
                bg="white"
                p={10}
                borderRadius="20px"
                boxShadow="0 8px 32px rgba(0, 0, 0, 0.12)"
                transition="all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
                border="2px solid"
                borderColor="transparent"
                position="relative"
                overflow="hidden"
                minH="320px"
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  bgGradient: category.bgGradient,
                  transition: 'all 0.3s',
                }}
                _hover={{
                  transform: 'translateY(-12px) scale(1.02)',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
                  borderColor: category.color === 'yellow' ? 'yellow.400' :
                              category.color === 'green' ? 'green.400' :
                              category.color === 'blue' ? 'blue.400' : 'purple.400',
                  _before: {
                    height: '6px',
                  },
                }}
              >
                {/* Icon */}
                <Box
                  display="inline-flex"
                  p={5}
                  borderRadius="18px"
                  bgGradient={category.bgGradient}
                  mb={6}
                  boxShadow="0 8px 25px rgba(0, 0, 0, 0.15)"
                  position="relative"
                  _after={{
                    content: '""',
                    position: 'absolute',
                    inset: '-2px',
                    borderRadius: '20px',
                    padding: '2px',
                    background: category.bgGradient,
                    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    maskComposite: 'subtract',
                    opacity: 0.3,
                  }}
                >
                  <Icon
                    as={category.icon}
                    boxSize={10}
                    color="white"
                    filter="drop-shadow(0 2px 4px rgba(0,0,0,0.2))"
                  />
                </Box>

                {/* Title */}
                <Heading
                  as="h3"
                  fontSize="2xl"
                  fontWeight="700"
                  color="gray.800"
                  mb={6}
                  position="relative"
                  _after={{
                    content: '""',
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '40px',
                    height: '3px',
                    bg: category.color === 'yellow' ? 'yellow.400' :
                        category.color === 'green' ? 'green.400' :
                        category.color === 'blue' ? 'blue.400' : 'purple.400',
                    borderRadius: '2px',
                  }}
                >
                  {category.title}
                </Heading>

                {/* Skills Tags */}
                <Wrap spacing={3} mt={2}>
                  {category.skills.map((skill, skillIndex) => (
                    <WrapItem key={skillIndex}>
                      {typeof skill === 'string' ? (
                        <Tag
                          size="md"
                          variant="subtle"
                          colorScheme={category.color}
                          borderRadius="full"
                          px={5}
                          py={3}
                          fontWeight="600"
                          fontSize="sm"
                          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                          position="relative"
                          border="1px solid"
                          borderColor="transparent"
                          _hover={{
                            transform: 'translateY(-2px) scale(1.05)',
                            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                            borderColor: category.color === 'yellow' ? 'yellow.300' :
                                        category.color === 'green' ? 'green.300' :
                                        category.color === 'blue' ? 'blue.300' : 'purple.300',
                          }}
                        >
                          {skill}
                        </Tag>
                      ) : (
                        <Tag
                          as={Link}
                          href={skill.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="md"
                          variant="subtle"
                          colorScheme={category.color}
                          borderRadius="full"
                          px={5}
                          py={3}
                          fontWeight="600"
                          fontSize="sm"
                          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                          position="relative"
                          border="1px solid"
                          borderColor="transparent"
                          _hover={{
                            transform: 'translateY(-2px) scale(1.05)',
                            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                            borderColor: category.color === 'yellow' ? 'yellow.300' :
                                        category.color === 'green' ? 'green.300' :
                                        category.color === 'blue' ? 'blue.300' : 'purple.300',
                            textDecoration: 'none',
                          }}
                          cursor="pointer"
                        >
                          {skill.name}
                        </Tag>
                      )}
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

export default Skills;
